import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  CircularProgress
} from '@mui/material';
import Grid from '../common/Grid';
import {
  Calculate as CalculateIcon,
  Science as ScienceIcon,
  CheckCircle as CheckIcon
} from '@mui/icons-material';
import {
  createUnifiedHydroBuddyEngine,
  UnifiedCalculationOptions,
  normalizePurity,
  elementPpmFromSaltGrams,
} from '../../utils/UnifiedHydroBuddyEngine';
import {
  getSaltComposition,
  normalizeElementSymbol,
  elementsMatch,
} from '../../utils/saltElements';
import { ECCalculationService } from '../../services/ECCalculationService';

// Type alias for backward compatibility
type CalculationOptions = UnifiedCalculationOptions;

interface Salt {
  id: string;
  name: string;
  formula: string;
  elements: { symbol: string; percentage: number }[];
  solubility?: number;
  density?: number;
  purity?: number;
}

interface StockCalculationInput {
  salts: Salt[];
  stockType: 'single' | 'partA' | 'partB' | 'partAB';
  stockVolume: number;
  volumeUnit: string;
  concentrationFactor: number;
  temperature: number;
  targetConcentrations: { [element: string]: number };
  /** Stage 1 water background ions (ppm) — subtracted on matrix RHS like Pascal */
  waterQuality?: {
    elements?: { symbol: string; concentration: number }[];
    ph?: number;
    ec?: number;
  };
  degreeOfFreedomElement?: string; // Optional DOF element
  safetyMargin?: number; // Safety margin for solubility (0-1), default 0.9
  temperatureCoefficient?: number; // Temperature coefficient for solubility adjustment, default 0.02
}

interface StockCalculationResult {
  saltAmounts: {
    id: string;
    name: string;
    amount: number;
    unit: string;
    status: 'ok' | 'warning' | 'error';
    solubilityCheck: boolean;
  }[];
  stockSolution: {
    volume: number;
    totalDissolvedSolids: number;
    estimatedEC: number;
    estimatedPH: number;
    dilutionRatio: number;
  };
  /** Working-solution nutrient balance (from HydroBuddy matrix) */
  achievedConcentrations?: {
    symbol: string;
    target: number;
    achieved: number;
    error?: number;
    errorPercent?: number;
    difference?: number;
  }[];
  solutionProperties?: {
    electricalConductivity: number;
    finalPH: number;
    totalDissolvedSolids: number;
    ionicStrength?: number;
  };
  preparationSteps: string[];
  qualityChecks: string[];
  warnings: string[];
}

interface ProfessionalStockCalculatorProps {
  input: StockCalculationInput;
  onCalculationComplete: (result: StockCalculationResult) => void;
}

const ProfessionalStockCalculator: React.FC<ProfessionalStockCalculatorProps> = ({
  input,
  onCalculationComplete
}) => {
  const [calculating, setCalculating] = useState(false);
  const [result, setResult] = useState<StockCalculationResult | null>(null);
  const [temperature, setTemperature] = useState(input.temperature || 20);
  const [concentrationFactor, setConcentrationFactor] = useState(input.concentrationFactor || 100);

  // Disable auto-calculation to prevent infinite loops - user must click button
  // const targetCount = useMemo(() => Object.keys(input.targetConcentrations || {}).length, [input.targetConcentrations]);

  // useEffect(() => {
  //   if (input.salts && input.salts.length > 0 &&
  //       targetCount > 0 &&
  //       !calculating && !result) { // Only calculate if not already calculating and no result
  //     calculateStockSolution();
  //   }
  // }, [input.salts?.length, targetCount, calculating, result]);

  // Professional stock solution calculation based on HydroBuddy algorithms
  const calculateStockSolution = async () => {
    setCalculating(true);

    try {
      // Removed excessive debug logging to prevent console spam
      // Only log critical errors in development mode
      if (process.env.NODE_ENV === 'development' && (!input.salts || !input.targetConcentrations)) {
        console.warn('ProfessionalStockCalculator - Missing required input data');
      }

      // Validate input data
      if (!input.salts || input.salts.length === 0) {
        throw new Error('No salts provided for calculation');
      }

      if (!input.targetConcentrations || Object.keys(input.targetConcentrations).length === 0) {
        throw new Error('No target concentrations provided for calculation');
      }

      // Working-solution targets (mg/L)
      const targetsArray = Object.entries(input.targetConcentrations)
        .map(([symbol, concentration]) => ({
          symbol: String(symbol).trim(),
          concentration: Number(concentration) || 0,
          unit: 'ppm' as const,
        }))
        .filter((t) => t.symbol && t.concentration > 0);

      // Flatten nested API salt shapes { element: { symbol }, percentage }
      const saltsData = input.salts.map((salt) => {
        const composition = getSaltComposition(salt);
        return {
          id: salt.id,
          name: salt.name,
          formula: salt.formula,
          purity: normalizePurity(salt.purity ?? 1),
          isLiquid: Boolean((salt as any).isLiquid),
          solubility: salt.solubility,
          density: salt.density,
          elements: composition.map((el) => ({
            symbol: el.symbol,
            percentage: el.percentage,
          })),
        };
      });

      const saltsWithComposition = saltsData.filter((s) => s.elements.length > 0);
      if (saltsWithComposition.length === 0) {
        throw new Error(
          'Selected salts have no element composition (%). Re-select complete salts from the library.'
        );
      }

      const targetsData = { elements: targetsArray };
      if (targetsData.elements.length === 0) {
        throw new Error('No target elements provided for calculation');
      }

      // Pascal: waterquality[i] subtracted from target on RHS
      const waterQualityData = {
        elements: (input.waterQuality?.elements || [])
          .map((e) => ({
            symbol: String(e.symbol || '').trim(),
            concentration: Number(e.concentration) || 0,
          }))
          .filter((e) => e.symbol && e.concentration > 0),
        ph: input.waterQuality?.ph ?? 6.5,
        ec: input.waterQuality?.ec ?? 0,
      };

      // Solve for stock tank volume at WORKING-solution setpoints, then scale by CF
      // (Pascal A+B path: solve then multiply amounts by concentration factor)
      const optionsData: CalculationOptions = {
        volume: input.stockVolume,
        volumeUnit: 'L',
        concentrationUnit: 'ppm',
        degreeOfFreedomElement: (input.degreeOfFreedomElement as any) || 'S',
        massUnit: 'g',
        precision: {
          decimalPlaces: 3,
          tolerance: 0.01,
          weightError: 0.01,
          volumeError: 0.01,
          macronutrientTolerance: 0.05,
          micronutrientTolerance: 0.1,
          convergenceTolerance: 1e-12,
        },
        ecModel: 'lmcv2',
        stockSolutionMode: 'direct', // solve working; CF applied below
        concentrationFactor: 1,
        debug: process.env.NODE_ENV === 'development',
      };

      const calculationEngine = createUnifiedHydroBuddyEngine(
        saltsWithComposition as any,
        waterQualityData,
        targetsData,
        optionsData
      );

      const hydroBuddyResult = await calculationEngine.calculate();

      const cf = Math.max(1, concentrationFactor || input.concentrationFactor || 1);

      // Salt amounts for STOCK: working grams × CF (Pascal ~2362)
      const saltAmounts = (hydroBuddyResult.saltAmounts || []).map((saltAmount) => {
        const salt =
          input.salts.find((s) => s.id === saltAmount.id) ||
          saltsWithComposition.find((s) => s.id === saltAmount.id);

        const workingAmount = Math.max(0, Number(saltAmount.amount) || 0);
        const stockAmount = workingAmount * cf;

        const solubilityLimit = salt ? getSolubilityLimit(salt as Salt, temperature) : 200;
        const safetyMargin = input.safetyMargin ?? 0.9;
        const maxAmount = solubilityLimit * input.stockVolume * safetyMargin;

        let status: 'ok' | 'warning' | 'error' = 'ok';
        let finalAmount = stockAmount;

        if (stockAmount > maxAmount) {
          status = 'error';
          finalAmount = maxAmount;
        } else if (stockAmount > maxAmount * 0.8) {
          status = 'warning';
        }

        return {
          id: saltAmount.id,
          name: saltAmount.name,
          amount: finalAmount,
          unit: 'g',
          status,
          solubilityCheck: stockAmount <= maxAmount,
          // Preserve composition for report recompute
          elements: (salt as any)?.elements
            ? getSaltComposition(salt as any).map((e) => ({
                symbol: e.symbol,
                percentage: e.percentage,
              }))
            : saltsWithComposition.find((s) => s.id === saltAmount.id)?.elements || [],
          purity: normalizePurity((salt as any)?.purity ?? 1),
        };
      });

      // Achieved = WORKING solution after dilution (not stock tank ppm)
      // Prefer engine result; if empty/all-zero, recompute from working grams chemically
      let achievedConcentrations = (hydroBuddyResult.achievedConcentrations || []).map(
        (ac: any) => ({
          symbol: ac.symbol,
          target: Number(ac.target) || 0,
          achieved: Number(ac.achieved) || 0,
          error: Number(ac.error) || 0,
          errorPercent: Number(ac.errorPercent) || 0,
          difference: Number(ac.error ?? Number(ac.achieved) - Number(ac.target)) || 0,
        })
      );

      const allZero =
        achievedConcentrations.length === 0 ||
        achievedConcentrations.every((a) => !(a.achieved > 0));

      if (allZero) {
        achievedConcentrations = recomputeWorkingAchieved(
          saltAmounts,
          targetsArray,
          input.stockVolume,
          cf
        );
      }

      // Merge ALL symbols from targets + salt composition (side products e.g. NH₄ from MAP)
      {
        const bySym = new Map(
          achievedConcentrations.map((a) => [normalizeElementSymbol(a.symbol), a])
        );
        const extraSymbols = new Set<string>();
        for (const t of targetsArray) extraSymbols.add(t.symbol);
        for (const sa of saltAmounts) {
          for (const el of sa.elements || []) {
            if (el.symbol) extraSymbols.add(el.symbol);
          }
        }
        for (const sym of extraSymbols) {
          const targetRow = targetsArray.find(
            (t) => normalizeElementSymbol(t.symbol) === normalizeElementSymbol(sym)
          );
          const target = targetRow?.concentration ?? 0;
          const chem = recomputeWorkingAchieved(
            saltAmounts,
            [{ symbol: sym, concentration: target }],
            input.stockVolume,
            cf
          )[0];
          if (!chem) continue;
          const key = normalizeElementSymbol(sym);
          const existing = bySym.get(key);
          // Prefer chemical recompute when matrix row missing or for side-product ions
          if (!existing || (!(existing.achieved > 0) && chem.achieved > 0)) {
            bySym.set(key, chem);
          } else if (existing && chem.achieved > 0 && !targetRow) {
            // Side product not in formulation targets — keep chem
            bySym.set(key, { ...chem, target: 0 });
          }
        }
        achievedConcentrations = Array.from(bySym.values());
      }

      // EC of working solution from achieved ions
      const ecFromAchieved = ECCalculationService.calculate(
        achievedConcentrations.map((a) => ({
          symbol: a.symbol,
          concentration: Math.max(0, a.achieved),
        })),
        'LMCv2'
      );

      const totalDissolvedSolids = saltAmounts.reduce((sum, salt) => sum + salt.amount, 0);
      const estimatedEC =
        ecFromAchieved.predicted ||
        hydroBuddyResult.solutionProperties?.electricalConductivity ||
        0;
      const estimatedPH = hydroBuddyResult.solutionProperties?.estimatedPH || 6.5;

      const preparationSteps = generatePreparationSteps(saltAmounts, input);
      const qualityChecks = generateQualityChecks(input.stockType, estimatedEC, estimatedPH);
      const warnings = generateWarnings(saltAmounts, totalDissolvedSolids, input.stockVolume);

      if (saltsData.length > saltsWithComposition.length) {
        warnings.push(
          `${saltsData.length - saltsWithComposition.length} salt(s) skipped (no element composition in database).`
        );
      }

      const calculationResult: StockCalculationResult = {
        saltAmounts,
        stockSolution: {
          volume: input.stockVolume,
          totalDissolvedSolids,
          estimatedEC,
          estimatedPH,
          dilutionRatio: cf,
        },
        achievedConcentrations,
        solutionProperties: {
          electricalConductivity: estimatedEC,
          finalPH: estimatedPH,
          totalDissolvedSolids,
          ionicStrength:
            ecFromAchieved.ionicStrength ||
            hydroBuddyResult.solutionProperties?.ionicStrength,
        },
        preparationSteps,
        qualityChecks,
        warnings,
      };

      setResult(calculationResult);
    } catch (error) {
      // Better error handling with logging in development
      if (process.env.NODE_ENV === 'development') {
        console.error('ProfessionalStockCalculator - Calculation failed:', error);
      }

      // Create fallback result with zero amounts to show the issue
      const fallbackSaltAmounts = input.salts.map(salt => ({
        id: salt.id,
        name: salt.name,
        amount: 0,
        unit: 'g',
        status: 'error' as const,
        solubilityCheck: true
      }));

      const fallbackResult: StockCalculationResult = {
        saltAmounts: fallbackSaltAmounts,
        stockSolution: {
          volume: input.stockVolume,
          totalDissolvedSolids: 0,
          estimatedEC: 0,
          estimatedPH: 6.5,
          dilutionRatio: concentrationFactor,
        },
        achievedConcentrations: Object.entries(input.targetConcentrations || {}).map(
          ([symbol, target]) => ({
            symbol,
            target: Number(target) || 0,
            achieved: 0,
            error: -(Number(target) || 0),
            errorPercent: -100,
            difference: -(Number(target) || 0),
          })
        ),
        solutionProperties: {
          electricalConductivity: 0,
          finalPH: 6.5,
          totalDissolvedSolids: 0,
        },
        preparationSteps: ['Calculation failed - please check input data'],
        qualityChecks: ['Unable to calculate - check salt and target data'],
        warnings: [
          `Calculation error: ${error instanceof Error ? error.message : 'Unknown error'}`,
        ],
      };

      setResult(fallbackResult);
      // Don't auto-advance - let user click the button to proceed
    } finally {
      setCalculating(false);
    }
  };

  /**
   * Recompute working-solution ppm from stock salt grams.
   * stock_g / CF = working grams for the same tank volume after dilution.
   * ppm = g_working × % × purity_frac × 10 / volume_L
   */
  const recomputeWorkingAchieved = (
    saltAmounts: Array<{
      id: string;
      name: string;
      amount: number;
      elements?: { symbol: string; percentage: number }[];
      purity?: number;
    }>,
    targets: Array<{ symbol: string; concentration: number }>,
    volumeL: number,
    cf: number
  ) => {
    const factor = Math.max(1, cf);
    return targets.map((t) => {
      let achieved = 0;
      for (const sa of saltAmounts) {
        const workingGrams = (Number(sa.amount) || 0) / factor;
        const purity = normalizePurity(sa.purity ?? 1);
        const elements =
          sa.elements && sa.elements.length > 0
            ? sa.elements
            : getSaltComposition(
                input.salts.find((s) => s.id === sa.id) || { elements: [] }
              ).map((e) => ({ symbol: e.symbol, percentage: e.percentage }));

        for (const el of elements) {
          // Strict form match: N_NO3 ≠ N_NH4 ≠ plain N (total)
          if (elementsMatch(el.symbol, t.symbol)) {
            achieved += elementPpmFromSaltGrams(
              workingGrams,
              el.percentage,
              purity,
              volumeL
            );
          }
        }
      }
      const target = Number(t.concentration) || 0;
      const error = achieved - target;
      return {
        symbol: t.symbol,
        target,
        achieved,
        error,
        errorPercent: target > 0 ? (error / target) * 100 : 0,
        difference: error,
      };
    });
  };

  const getSolubilityLimit = (salt: Salt, temp: number): number => {
    // Temperature-adjusted solubility using actual salt data
    const baseSolubility = salt.solubility || 200; // Use salt's actual solubility or reasonable default
    const tempCoefficient = input.temperatureCoefficient || 0.02; // User-configurable temperature coefficient
    const tempFactor = 1 + (temp - 20) * tempCoefficient;
    return baseSolubility * tempFactor;
  };

  const calculateElectricalConductivity = (saltAmounts: any[], salts: Salt[]): number => {
    // Use centralized ECCalculationService for accurate Pascal HydroBuddy algorithms
    const elementConcentrations: { symbol: string; concentration: number }[] = [];

    saltAmounts.forEach(saltAmount => {
      const salt = salts.find(s => s.id === saltAmount.id);
      if (salt && salt.elements) {
        salt.elements.forEach(element => {
          // Calculate element concentration in ppm
          const elementSymbol = element.symbol;
          const concentrationPpm = (saltAmount.amount * element.percentage) / (100 * input.stockVolume);

          // Add to element concentrations array
          const existingElement = elementConcentrations.find(e => e.symbol === elementSymbol);
          if (existingElement) {
            existingElement.concentration += concentrationPpm;
          } else {
            elementConcentrations.push({ symbol: elementSymbol, concentration: concentrationPpm });
          }
        });
      }
    });

    // Use ECCalculationService for accurate calculation
    const ecResult = ECCalculationService.calculate(elementConcentrations, 'LMCv2');
    return ecResult.predicted;
  };

  const estimatePH = (saltAmounts: any[], salts: Salt[]): number => {
    // Simplified pH estimation
    let acidicContribution = 0;
    let basicContribution = 0;
    
    saltAmounts.forEach(saltAmount => {
      const salt = salts.find(s => s.id === saltAmount.id);
      if (salt) {
        // Check for acidic/basic elements
        if (salt.elements.some(e => ['P', 'S', 'N'].includes(e.symbol))) {
          acidicContribution += saltAmount.amount;
        }
        if (salt.elements.some(e => ['K', 'Ca', 'Mg'].includes(e.symbol))) {
          basicContribution += saltAmount.amount;
        }
      }
    });
    
    const ratio = acidicContribution / (basicContribution + acidicContribution + 1);
    return 6.5 - (ratio * 1.5); // Simplified pH estimation
  };

  const generatePreparationSteps = (saltAmounts: any[], input: StockCalculationInput): string[] => {
    const steps = [];
    
    steps.push(`Prepare ${input.stockVolume}${input.volumeUnit} of ${input.stockType} stock solution`);
    steps.push('Use distilled or deionized water');
    steps.push('Fill container to 80% of final volume');
    
    // Sort salts by solubility (most soluble first)
    const sortedSalts = [...saltAmounts].sort((a, b) => {
      const saltA = input.salts.find(s => s.id === a.id);
      const saltB = input.salts.find(s => s.id === b.id);
      return (saltB?.solubility || 100) - (saltA?.solubility || 100);
    });
    
    sortedSalts.forEach((saltAmount, index) => {
      steps.push(`${index + 4}. Add ${saltAmount.amount.toFixed(2)}g of ${saltAmount.name}`);
      steps.push(`   Stir until completely dissolved before adding next salt`);
    });
    
    steps.push(`Fill to final volume (${input.stockVolume}${input.volumeUnit})`);
    steps.push('Mix thoroughly for 5-10 minutes');
    steps.push('Allow to equilibrate for 30 minutes before use');
    
    return steps;
  };

  const generateQualityChecks = (stockType: string, ec: number, ph: number): string[] => {
    return [
      `Measure pH: Expected ~${ph.toFixed(1)} (acceptable range: ${(ph - 0.5).toFixed(1)} - ${(ph + 0.5).toFixed(1)})`,
      `Measure EC: Expected ~${ec.toFixed(1)} mS/cm`,
      'Check solution clarity (should be clear, no precipitation)',
      'Verify temperature is 18-24°C for optimal stability',
      'Label with preparation date and concentration factor',
      'Store in cool, dark location away from direct sunlight'
    ];
  };

  const generateWarnings = (saltAmounts: any[], totalSolids: number, volume: number): string[] => {
    const warnings = [];
    
    const concentration = totalSolids / volume;
    if (concentration > 200) {
      warnings.push('High concentration stock solution - monitor for precipitation');
    }
    
    const errorSalts = saltAmounts.filter(s => s.status === 'error');
    if (errorSalts.length > 0) {
      warnings.push(`Solubility exceeded for: ${errorSalts.map(s => s.name).join(', ')}`);
    }
    
    const warningSalts = saltAmounts.filter(s => s.status === 'warning');
    if (warningSalts.length > 0) {
      warnings.push(`Near solubility limit: ${warningSalts.map(s => s.name).join(', ')}`);
    }
    
    return warnings;
  };

  return (
    <Card>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          Professional Stock Solution Calculator
        </Typography>
        
        {/* Calculation Parameters */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid xs={12} md={6}>
            <TextField
              fullWidth
              label="Temperature (°C)"
              type="number"
              value={temperature}
              onChange={(e) => setTemperature(Number(e.target.value))}
              InputProps={{ inputProps: { min: 5, max: 40 } }}
              helperText="Affects salt solubility"
            />
          </Grid>
          <Grid xs={12} md={6}>
            <TextField
              fullWidth
              label="Concentration Factor"
              type="number"
              value={concentrationFactor}
              onChange={(e) => setConcentrationFactor(Number(e.target.value))}
              InputProps={{ inputProps: { min: 10, max: 1000 } }}
              helperText="Stock concentration multiplier"
            />
          </Grid>
        </Grid>

        {/* Calculate Button */}
        <Box sx={{ mb: 3 }}>
          <Button
            variant="contained"
            onClick={calculateStockSolution}
            disabled={calculating}
            startIcon={calculating ? <CircularProgress size={20} /> : <CalculateIcon />}
            size="large"
          >
            {calculating ? 'Calculating...' : 'Calculate Stock Solution'}
          </Button>
        </Box>

        {/* Results */}
        {result && (
          <Box>
            {/* Warnings */}
            {result.warnings.length > 0 && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                <Typography variant="body2" gutterBottom>
                  <strong>Warnings:</strong>
                </Typography>
                {result.warnings.map((warning, index) => (
                  <Typography key={index} variant="body2">
                    • {warning}
                  </Typography>
                ))}
              </Alert>
            )}

            {/* Salt Amounts Table */}
            <TableContainer component={Paper} variant="outlined" sx={{ mb: 2 }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Salt</TableCell>
                    <TableCell align="right">Amount (g)</TableCell>
                    <TableCell align="center">Status</TableCell>
                    <TableCell align="center">Solubility OK</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {result.saltAmounts.map((salt) => (
                    <TableRow key={salt.id}>
                      <TableCell>{salt.name}</TableCell>
                      <TableCell align="right">{salt.amount.toFixed(2)}</TableCell>
                      <TableCell align="center">
                        <Chip
                          size="small"
                          label={salt.status}
                          color={salt.status === 'ok' ? 'success' : salt.status === 'warning' ? 'warning' : 'error'}
                        />
                      </TableCell>
                      <TableCell align="center">
                        {salt.solubilityCheck ? <CheckIcon color="success" /> : '⚠️'}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            {/* Stock Solution Properties */}
            <Card variant="outlined" sx={{ mb: 2 }}>
              <CardContent>
                <Typography variant="subtitle1" gutterBottom>
                  Stock Solution Properties
                </Typography>
                <Grid container spacing={2}>
                  <Grid xs={6} md={3}>
                    <Typography variant="body2" color="text.secondary">
                      Volume
                    </Typography>
                    <Typography variant="body1">
                      {result.stockSolution.volume} {input.volumeUnit}
                    </Typography>
                  </Grid>
                  <Grid xs={6} md={3}>
                    <Typography variant="body2" color="text.secondary">
                      Total Solids
                    </Typography>
                    <Typography variant="body1">
                      {result.stockSolution.totalDissolvedSolids.toFixed(1)} g
                    </Typography>
                  </Grid>
                  <Grid xs={6} md={3}>
                    <Typography variant="body2" color="text.secondary">
                      Estimated EC
                    </Typography>
                    <Typography variant="body1">
                      {result.stockSolution.estimatedEC.toFixed(1)} mS/cm
                    </Typography>
                  </Grid>
                  <Grid xs={6} md={3}>
                    <Typography variant="body2" color="text.secondary">
                      Estimated pH
                    </Typography>
                    <Typography variant="body1">
                      {result.stockSolution.estimatedPH.toFixed(1)}
                    </Typography>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>

            {/* Proceed to A/B Separation Button */}
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
              <Button
                variant="contained"
                size="large"
                onClick={() => {
                  if (onCalculationComplete && typeof onCalculationComplete === 'function') {
                    onCalculationComplete(result);
                  }
                }}
                startIcon={<CheckIcon />}
              >
                Proceed to A/B Tank Separation
              </Button>
            </Box>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

export default ProfessionalStockCalculator;
